import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from '@/components/ui/card';
import { 
  Tabs, 
  TabsList, 
  TabsTrigger, 
  TabsContent 
} from '@/components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, BarChart2, PieChart, TrendingUp } from 'lucide-react';
import { 
  fetchAgents, 
  fetchLeadSources, 
  fetchAgentwiseAnalytics, 
  fetchLeadSourceAnalytics 
} from '@/lib/apiService';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  PieChart as RechartsaPieChart, Pie, Cell, LineChart, Line, CartesianGrid
} from 'recharts';

const AnalyticsPage = () => {
  // State for filters
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [selectedLeadSource, setSelectedLeadSource] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('daily');
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(new Date().getDate() - 30)), // Last 30 days
    to: new Date()
  });
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);

  // State for dropdown options
  const [agentNames, setAgentNames] = useState([]);
  const [leadSources, setLeadSources] = useState([]);

  // State for analytics data
  const [agentAnalytics, setAgentAnalytics] = useState([]);
  const [sourceAnalytics, setSourceAnalytics] = useState([]);
  const [statusAnalytics, setStatusAnalytics] = useState([]);

  // Loading states
  const [loadingAgentAnalytics, setLoadingAgentAnalytics] = useState(false);
  const [loadingSourceAnalytics, setLoadingSourceAnalytics] = useState(false);

  // Color palette for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

  // Load dropdown data on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [agentNamesData, leadSourcesData] = await Promise.all([
          fetchAgents(),
          fetchLeadSources()
        ]);

        // Ensure data exists and has expected structure
        if (Array.isArray(agentNamesData)) {
          setAgentNames(agentNamesData);
        } else {
          console.error("Agent names data is not an array:", agentNamesData);
          setAgentNames([]);
        }

        if (Array.isArray(leadSourcesData)) {
          setLeadSources(leadSourcesData);
        } else {
          console.error("Lead sources data is not an array:", leadSourcesData);
          setLeadSources([]);
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
        setAgentNames([]);
        setLeadSources([]);
      }
    };

    loadInitialData();
  }, []);

  // Load agent analytics when filters change
  useEffect(() => {
    const loadAgentAnalytics = async () => {
      if (selectedAgent === 'all') return; // Skip if "All" is selected

      try {
        setLoadingAgentAnalytics(true);
        const startDateStr = format(dateRange.from, 'yyyy-MM-dd');
        const endDateStr = format(dateRange.to, 'yyyy-MM-dd');

        const analytics = await fetchAgentwiseAnalytics(
          selectedAgent,
          startDateStr,
          endDateStr,
          selectedPeriod
        );

        setAgentAnalytics(analytics || []);

        if (analytics && analytics.length > 0) {
          const statusData = [];
          const aggregatedStatuses = {};

          analytics.forEach(period => {
            if (period.statuses) {
              Object.entries(period.statuses).forEach(([status, count]) => {
                aggregatedStatuses[status] = (aggregatedStatuses[status] || 0) + count;
              });
            }
          });

          Object.entries(aggregatedStatuses).forEach(([status, count]) => {
            statusData.push({ name: status, value: count });
          });

          setStatusAnalytics(statusData);
        } else {
          setStatusAnalytics([]);
        }
      } catch (error) {
        console.error("Error loading agent analytics:", error);
        setAgentAnalytics([]);
        setStatusAnalytics([]);
      } finally {
        setLoadingAgentAnalytics(false);
      }
    };

    loadAgentAnalytics();
  }, [selectedAgent, dateRange, selectedPeriod]);

  // Load source analytics when filters change
  useEffect(() => {
    const loadSourceAnalytics = async () => {
      if (selectedLeadSource === 'all') return; // Skip if "All" is selected

      try {
        setLoadingSourceAnalytics(true);
        const startDateStr = format(dateRange.from, 'yyyy-MM-dd');
        const endDateStr = format(dateRange.to, 'yyyy-MM-dd');

        const analytics = await fetchLeadSourceAnalytics(
          selectedLeadSource,
          startDateStr,
          endDateStr
        );

        if (analytics && Array.isArray(analytics)) {
          const timeSeriesData = analytics.map(item => ({
            date: item.date,
            count: item.count || 0
          })).sort((a, b) => new Date(a.date) - new Date(b.date));

          setSourceAnalytics(timeSeriesData);
        } else {
          setSourceAnalytics([]);
        }
      } catch (error) {
        console.error("Error loading source analytics:", error);
        setSourceAnalytics([]);
      } finally {
        setLoadingSourceAnalytics(false);
      }
    };

    loadSourceAnalytics();
  }, [selectedLeadSource, dateRange]);

  // Handle date range selection
  const handleDateRangeSelect = (range) => {
    setDateRange(range);
    if (range.from && range.to) {
      setShowDateRangePicker(false);
    }
  };

  // Format agent analytics data for visualization
  const formatAgentSourceData = () => {
    if (!agentAnalytics || agentAnalytics.length === 0) return [];

    return agentAnalytics.map(period => {
      const formattedData = { period: period.period };
      if (period.sources) {
        Object.entries(period.sources).forEach(([source, count]) => {
          formattedData[source] = count;
        });
      }
      return formattedData;
    });
  };

  // Check if agent data has name property, otherwise use id as name
  const getAgentName = (agent) => {
    if (typeof agent === 'string') return agent;
    
    // Check if the agent has a name property
    if (agent && agent.name) return agent.name;
    
    // Fallback to id if name doesn't exist
    if (agent && agent.id) return String(agent.id);
    
    // Ultimate fallback
    return 'Unknown Agent';
  };

  // Check if source data has name property, otherwise use id as name
  const getSourceName = (source) => {
    if (typeof source === 'string') return source;
    
    // Check if the source has a name property
    if (source && source.name) return source.name;
    
    // Fallback to id if name doesn't exist
    if (source && source.id) return String(source.id);
    
    // Ultimate fallback
    return 'Unknown Source';
  };

  return (
    <div className="space-y-6">
      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle>Analytics Filters</CardTitle>
          <CardDescription>Select filters to view specific analytics</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Date Range Picker */}
            <div>
              <label className="text-sm font-medium mb-1 block">Date Range</label>
              <Popover open={showDateRangePicker} onOpenChange={setShowDateRangePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.from ? (
                      dateRange.to ? (
                        `${format(dateRange.from, "dd MMM yyyy")} - ${format(dateRange.to, "dd MMM yyyy")}`
                      ) : (
                        format(dateRange.from, "dd MMM yyyy")
                      )
                    ) : (
                      "Select date range"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={handleDateRangeSelect}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Agent Dropdown */}
            <div>
              <label className="text-sm font-medium mb-1 block">Select Agent</label>
              <Select value={selectedAgent} onValueChange={setSelectedAgent}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="all-agents" value="all">All Agents</SelectItem>
                  {agentNames.map((agent) => (
                    <SelectItem 
                      key={agent.id || `agent-${Math.random().toString(36).substr(2, 9)}`} 
                      value={getAgentName(agent)}
                    >
                      {getAgentName(agent)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Lead Source Dropdown */}
            <div>
              <label className="text-sm font-medium mb-1 block">Select Lead Source</label>
              <Select value={selectedLeadSource} onValueChange={setSelectedLeadSource}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Lead Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="all-sources" value="all">All Sources</SelectItem>
                  {leadSources.map((source) => (
                    <SelectItem 
                      key={source.id || `source-${Math.random().toString(36).substr(2, 9)}`} 
                      value={getSourceName(source)}
                    >
                      {getSourceName(source)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Period Selector */}
            <div>
              <label className="text-sm font-medium mb-1 block">Time Period</label>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="daily-period" value="daily">Daily</SelectItem>
                  <SelectItem key="weekly-period" value="weekly">Weekly</SelectItem>
                  <SelectItem key="monthly-period" value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Analytics Tabs */}
      <Tabs defaultValue="agent">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="agent" className="flex items-center">
            <BarChart2 className="h-4 w-4 mr-2" /> Agent Analytics
          </TabsTrigger>
          <TabsTrigger value="source" className="flex items-center">
            <PieChart className="h-4 w-4 mr-2" /> Lead Source Analytics
          </TabsTrigger>
          <TabsTrigger value="status" className="flex items-center">
            <TrendingUp className="h-4 w-4 mr-2" /> Lead Status Analytics
          </TabsTrigger>
        </TabsList>

        {/* Agent Analytics Tab */}
        <TabsContent value="agent">
          <Card>
            <CardHeader>
              <CardTitle>Agent Performance by Lead Source</CardTitle>
              <CardDescription>
                {selectedAgent === 'all' 
                  ? 'Showing analytics for all agents' 
                  : `Showing ${selectedPeriod} analytics for ${selectedAgent}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAgentAnalytics ? (
                <div className="h-80 flex items-center justify-center">
                  <div className="text-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent mx-auto"></div>
                    <p className="mt-2">Loading analytics...</p>
                  </div>
                </div>
              ) : agentAnalytics.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={formatAgentSourceData()} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" angle={-45} textAnchor="end" height={70} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {agentAnalytics[0]?.sources && Object.keys(agentAnalytics[0].sources).map((source, index) => (
                      <Bar key={`source-${source}`} dataKey={source} stackId="a" fill={COLORS[index % COLORS.length]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-80 flex items-center justify-center">
                  <p className="text-muted-foreground">
                    {selectedAgent === 'all' 
                      ? 'Please select a specific agent to view analytics' 
                      : 'No data available for the selected filters'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lead Source Analytics Tab */}
        <TabsContent value="source">
          <Card>
            <CardHeader>
              <CardTitle>Lead Source Performance Over Time</CardTitle>
              <CardDescription>
                {selectedLeadSource === 'all' 
                  ? 'Showing analytics for all lead sources' 
                  : `Showing analytics for ${selectedLeadSource}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingSourceAnalytics ? (
                <div className="h-80 flex items-center justify-center">
                  <div className="text-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent mx-auto"></div>
                    <p className="mt-2">Loading analytics...</p>
                  </div>
                </div>
              ) : sourceAnalytics.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={sourceAnalytics} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" angle={-45} textAnchor="end" height={70} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="count" stroke="#8884d8" activeDot={{ r: 8 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-80 flex items-center justify-center">
                  <p className="text-muted-foreground">
                    {selectedLeadSource === 'all' 
                      ? 'Please select a specific lead source to view analytics' 
                      : 'No data available for the selected filters'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Lead Status Analytics Tab */}
        <TabsContent value="status">
          <Card>
            <CardHeader>
              <CardTitle>Lead Status Distribution</CardTitle>
              <CardDescription>
                {selectedAgent === 'all' 
                  ? 'Showing lead status distribution for all agents' 
                  : `Showing lead status distribution for ${selectedAgent}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingAgentAnalytics ? (
                <div className="h-80 flex items-center justify-center">
                  <div className="text-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent mx-auto"></div>
                    <p className="mt-2">Loading analytics...</p>
                  </div>
                </div>
              ) : statusAnalytics.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pie Chart */}
                  <div>
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsaPieChart>
                        <Pie
                          data={statusAnalytics}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {statusAnalytics.map((entry, index) => (
                            <Cell key={`cell-${entry.name}-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsaPieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Bar Chart */}
                  <div>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={statusAnalytics} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="name" />
                        <Tooltip />
                        <Bar dataKey="value" fill="#8884d8">
                          {statusAnalytics.map((entry, index) => (
                            <Cell key={`bar-cell-${entry.name}-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div className="h-80 flex items-center justify-center">
                  <p className="text-muted-foreground">
                    {selectedAgent === 'all' 
                      ? 'Please select a specific agent to view status analytics' 
                      : 'No data available for the selected filters'}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsPage;